"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appendContentSecurityPolicyMeta = exports.HeadMeta = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_helmet_1 = require("react-helmet");
const web_headers_1 = require("@snapchat/web-headers");
const web_i18n_1 = require("@snapchat/web-i18n");
const utils_1 = require("./utils");
const SNAPCHAT_IOS_APP_ID = "447188370";
/**
 *  Re-usable Document Head components for Snapchat Web
 */
function HeadMeta({ lang, direction, title = "Snapchat", policyDirectives, hrefLangTagOptions, description, metricsPageName, canonicalUrl, robotsMeta = [], gaid, preconnects = [], preloads = [], manifestPath, snapchatDeepLinkUrl, theme, videoMetadata, }) {
    const validTheme = theme === "light" || theme === "dark" ? theme : "";
    const htmlAttributes = {
        prefix: "og: https://ogp.me/ns#",
        lang,
        "data-theme-override": validTheme,
        dir: direction ?? web_i18n_1.LanguageDirection.LTR,
    };
    return ((0, jsx_runtime_1.jsxs)(react_helmet_1.Helmet, { htmlAttributes: htmlAttributes, children: [(0, jsx_runtime_1.jsx)("meta", { charSet: "UTF-8" }), (0, jsx_runtime_1.jsx)("title", { children: title }), (0, jsx_runtime_1.jsx)("meta", { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1.0, user-scalable=0" }), metricsPageName && (0, jsx_runtime_1.jsx)("meta", { name: "metrics-page-name", content: metricsPageName }), policyDirectives && appendContentSecurityPolicyMeta(policyDirectives), appendGAScriptSrc(gaid), appendGA4ScriptSrc(gaid), preconnects.map(appendPreConnect), preloads.map(appendPreLoad), description && (0, jsx_runtime_1.jsx)("meta", { name: "description", content: description }), canonicalUrl && (0, jsx_runtime_1.jsx)("link", { rel: "canonical", href: canonicalUrl }), hrefLangTagOptions && appendHreflangTags(hrefLangTagOptions), robotsMeta.map(appendRobot), appendManifest(manifestPath), appendiOSSmartBanner(snapchatDeepLinkUrl), appendFavicon(), appendVideObjectMarkup(videoMetadata), appendGAInitScript(gaid), appendGA4InitScript(gaid), appendThemeScript()] }));
}
exports.HeadMeta = HeadMeta;
/**
 * Add HTML CSP meta given the directives
 */
function appendContentSecurityPolicyMeta(policyDirectives) {
    // filtering out "frame-ancestors directive because it is ignored when delivered via a <meta> element.
    const filtered = Object.keys(policyDirectives).reduce((acc, curr) => ({
        ...acc,
        ...(curr !== "frame-ancestors" && {
            [curr]: policyDirectives[curr],
        }),
    }), {});
    return (0, jsx_runtime_1.jsx)("meta", { httpEquiv: "Content-Security-Policy", content: (0, web_headers_1.generateCSPHeaderValue)(filtered) });
}
exports.appendContentSecurityPolicyMeta = appendContentSecurityPolicyMeta;
function appendGAScriptSrc(gaid) {
    if (gaid?.UAId) {
        return (0, jsx_runtime_1.jsx)("script", { async: true, src: "https://www.google-analytics.com/analytics.js" });
    }
}
function appendGA4ScriptSrc(gaid) {
    if (gaid?.measurementId) {
        const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${gaid.measurementId}`;
        return (0, jsx_runtime_1.jsx)("script", { async: true, src: scriptSrc });
    }
}
function appendPreConnect(preconnect, index) {
    return (0, jsx_runtime_1.jsx)("link", { rel: "preconnect", href: preconnect }, `${preconnect}+${index}`);
}
function appendPreLoad(preload) {
    return (0, jsx_runtime_1.jsx)("link", { rel: "preload", ...preload }, preload.href);
}
function appendRobot({ directive, name = "robots" }, index) {
    return (0, jsx_runtime_1.jsx)("meta", { name: name, content: directive }, `${directive}+${index}`);
}
function appendManifest(manifestPath) {
    if (manifestPath) {
        return (0, jsx_runtime_1.jsx)("link", { rel: "manifest", href: manifestPath });
    }
}
function appendHreflangTags({ template, locales = web_i18n_1.ALL_LOCALES, defaultValue }) {
    const tags = [];
    if (/{locale}/g.test(template)) {
        const formattedLocales = Array.from(new Set(locales.map((locale) => (0, web_i18n_1.getLocale)(locale))));
        tags.push(formattedLocales.map((formattedLocale) => {
            const href = template.replace(/{locale}/g, formattedLocale);
            return (0, jsx_runtime_1.jsx)("link", { rel: "alternate", href: href, hrefLang: formattedLocale }, href);
        }));
    }
    if (defaultValue) {
        tags.push((0, jsx_runtime_1.jsx)("link", { rel: "alternate", href: defaultValue, hrefLang: "x-default" }, "hreflang-x-default"));
    }
    return tags;
}
function appendiOSSmartBanner(appUrl) {
    const prefix = `app-id=${SNAPCHAT_IOS_APP_ID}`;
    const content = appUrl ? `${prefix}, app-argument=${appUrl}` : prefix;
    return (0, jsx_runtime_1.jsx)("meta", { name: "apple-itunes-app", content: content });
}
function appendFavicon() {
    return (0, jsx_runtime_1.jsx)("link", { rel: "shortcut icon", href: "https://static.snapchat.com/favicon.ico" });
}
function appendVideObjectMarkup(videoMetadata) {
    if (videoMetadata) {
        return (0, jsx_runtime_1.jsx)("script", { type: "application/ld+json", children: (0, utils_1.getVideoObjectWithContext)(videoMetadata) });
    }
}
function appendGAInitScript(gaid) {
    if (gaid?.UAId) {
        const src = `
    window.ga=window.ga||function(){(ga.q = ga.q || []).push(arguments)};
    ga.l=+new Date;
    ga('create','${gaid.UAId}', 'auto');
    ga('send','pageview', location.pathname);
  `;
        return (0, jsx_runtime_1.jsx)("script", { children: src });
    }
}
function appendGA4InitScript(gaid) {
    if (gaid?.measurementId) {
        const src = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaid.measurementId}');
  `;
        return (0, jsx_runtime_1.jsx)("script", { children: src });
    }
}
function appendThemeScript() {
    return ((0, jsx_runtime_1.jsx)("script", { children: `
      document.documentElement.setAttribute(
        "data-theme",
        document.documentElement.getAttribute("data-theme-override") ||
          (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"))
    ` }));
}
//# sourceMappingURL=index.js.map